import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";
import {
  PhotoThumbnailStrip,
  type PhotoThumbnailStripItem,
} from "@/components/common/photo-thumbnail-strip/photo-thumbnail-strip";
import {
  FilmstripStage,
  type FilmstripPhoto,
  type SlideZoomControls,
} from "./filmstrip-stage";
import { useFilmstripDrag } from "./use-filmstrip-drag";

/** One photo in the carousel — same tile shape the grid renders. */
export interface RecordPhotoViewerItem extends FilmstripPhoto {
  /**
   * The grid tile's thumbnail, for the strip. Absent falls back to the slide's
   * own `src` / `assetKey`, which on a synced record is the full-size image.
   */
  thumbnailSrc?: string;
  /** Offline-cache key for `thumbnailSrc`; empty skips the cache. */
  thumbnailAssetKey?: string;
}

export interface RecordPhotoViewerProps {
  photos: RecordPhotoViewerItem[];
  /** Index to open at. */
  initialIndex: number;
  onClose: () => void;
}

/**
 * Fullscreen swipeable carousel of one record's photos. Filmstrip swipe,
 * double-tap (+ pinch/scroll) zoom + pan per slide, chevron / keyboard
 * navigation, a counter, and a thumbnail strip that jumps straight to any
 * photo. Degrades to a single zoomable image with no chevrons and no strip
 * when the record has one photo. No Compare: that belongs to an issue.
 * See [[record-photo-viewer]].
 */
export function RecordPhotoViewer({
  photos,
  initialIndex,
  onClose,
}: RecordPhotoViewerProps) {
  const count = photos.length;
  const multi = count > 1;
  const clampedInitial = Math.min(
    Math.max(initialIndex, 0),
    Math.max(count - 1, 0),
  );

  const [zoomed, setZoomed] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const film = useFilmstripDrag({
    count,
    zoomed,
    initialIndex: clampedInitial,
    stageRef,
  });
  const { index, step, goTo } = film;

  // A committed navigation always lands on a fresh slide at rest scale; ensure
  // the zoom flag can never remain stuck across a photo change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setZoomed(false);
  }, [index]);

  const controlsRef = useRef<SlideZoomControls | null>(null);
  const registerControls = useCallback(
    (controls: SlideZoomControls | null) => {
      controlsRef.current = controls;
    },
    [],
  );

  // Keyboard: Escape closes (capture phase so the parent Dialog doesn't also
  // close), arrows step. Matches ExpandedImageView / ExpandedPlanView.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key === "ArrowRight") {
        step(1);
      } else if (e.key === "ArrowLeft") {
        step(-1);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, step]);

  const toggleZoom = () => {
    if (film.animating) return;
    if (zoomed) {
      controlsRef.current?.reset();
      setZoomed(false);
    } else {
      controlsRef.current?.zoomIn();
      setZoomed(true);
    }
  };

  // The strip shows the grid's thumbnails (and their offline keys) so a row of
  // tiles never pulls full-size photos; a slide without one falls back to its
  // own image. Checked for presence, not truthiness: offline a grid tile has an
  // empty address and resolves from its cache key alone.
  const thumbnails = useMemo<PhotoThumbnailStripItem[]>(
    () =>
      photos.map((photo) =>
        photo.thumbnailSrc !== undefined
          ? {
              id: photo.id,
              thumbnailSrc: photo.thumbnailSrc,
              thumbnailAssetKey: photo.thumbnailAssetKey,
            }
          : {
              id: photo.id,
              thumbnailSrc: photo.src,
              thumbnailAssetKey: photo.assetKey,
            },
      ),
    [photos],
  );

  const selectThumbnail = (target: number) => {
    if (target === index) return;
    // A thumbnail is an explicit choice, so it moves even while zoomed; reset
    // first so a neighbour that stays mounted is not left magnified.
    controlsRef.current?.reset();
    goTo(target);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-black"
      style={{ touchAction: "none" }}
      role="dialog"
      aria-label="Photo viewer"
      aria-modal="true"
    >
      {/* Top bar */}
      <div
        className="z-10 flex shrink-0 items-center gap-2.5 p-3"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 0.75rem)" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <span className="flex-1 text-center text-[13px] text-white/90">
          {index + 1} of {count}
        </span>
        <button
          type="button"
          onClick={toggleZoom}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label={zoomed ? "Zoom out" : "Zoom in"}
        >
          {zoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
        </button>
      </div>

      <FilmstripStage
        photos={photos}
        film={film}
        stageRef={stageRef}
        zoomed={zoomed}
        onZoomChange={setZoomed}
        registerControls={registerControls}
      >
        {/* Decorative hint */}
        <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[11px] tracking-[0.04em] text-white/50">
          {multi ? "swipe or arrows · double-tap to zoom" : "double-tap to zoom"}
        </span>
      </FilmstripStage>

      {/* Thumbnail strip — hidden when there is only one photo. */}
      {multi ? (
        <PhotoThumbnailStrip
          photos={thumbnails}
          currentIndex={index}
          label="Record photos"
          onSelect={selectThumbnail}
        />
      ) : (
        <div
          className="shrink-0"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        />
      )}
    </div>
  );
}
